<script setup lang="ts">
import type { ProductDetail, ProductLine, ProductSet } from '~/types/api'

const props = defineProps<{
  set: ProductSet | null
  products: ProductDetail[]
  selectedProduct: ProductDetail
  line: ProductLine
}>()

const emit = defineEmits(['update:selected-product'])

const route = useRoute()

const { getResourceUrl } = useResources()
</script>

<template>
  <UiSection gray>
    <template #title>
      <ProductTitleWithProductSelector
        title="base.products.setDetail.parameters.title"
        :products="products"
        :selected-product="selectedProduct"
        @update:selected-product="
          (product) => emit('update:selected-product', product)
        "
      />
    </template>
    <div
      class="grid grid-cols-2 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    >
      <div
        class="col-span-2 flex flex-col gap-4 rounded-sm bg-white p-8 sm:col-span-1"
      >
        <h3 class="font-pragmatica-ext text-2xl font-bold">
          {{ $t('base.products.setDetail.parameters.parameters') }}
        </h3>
        <ul
          class="font-pragmatica-ext flex list-disc flex-col gap-2 pl-4 text-xs font-bold"
        >
          <li
            v-for="item in selectedProduct.descriptionItems ?? []"
            :key="item?.nid"
          >
            {{ item.title }}
          </li>
          <li v-if="props.line.warranty">
            {{
              `${$t('base.products.setDetail.parameters.warranty')} ${props.line.warranty}`
            }}
          </li>
        </ul>
      </div>
      <div
        class="col-span-2 flex flex-col gap-4 rounded-sm bg-white p-8 sm:col-span-1"
      >
        <h3 class="font-pragmatica-ext text-2xl font-bold">
          {{ $t('base.products.setDetail.parameters.attributes') }}
        </h3>
        <div class="flex flex-col gap-2">
          <div
            v-for="property in props.selectedProduct.productProperties"
            :key="property?.nid"
            class="flex items-baseline justify-between text-xs"
          >
            <p class="font-pragmatica-ext pr-5 font-bold">
              {{ property.productPropertyType.title }}
            </p>
            <div class="whitespace-nowrap">
              {{ property.value + ' ' + property.productPropertyType.unit }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-for="image in selectedProduct.dimensionImages"
        :key="image?.nid"
        class="col-span-2 w-full gap-4 overflow-hidden rounded-sm bg-white sm:col-span-1"
      >
        <img
          class="h-full w-full object-cover"
          :src="getResourceUrl(image.url + '/1280x1280/FIT')"
          :alt="image?.metaAlt ?? undefined"
          :title="image?.metaTitle ?? undefined"
          loading="lazy"
        />
      </div>
      <div
        v-for="image in set?.bannerImages"
        :key="image?.nid"
        class="overflow-hidden bg-white"
        :class="
          image.imageRatioString === 'landscape'
            ? 'col-span-2'
            : 'col-span-2 sm:col-span-1'
        "
      >
        <img
          class="h-full w-full object-cover object-bottom"
          :src="getResourceUrl(image.url + '/1280x1280/FIT')"
          :alt="image?.metaAlt ?? undefined"
          :title="image?.metaTitle ?? undefined"
          loading="lazy"
        />
      </div>
    </div>
    <div
      v-if="props.line.footerDescription || props.line.fabricDescription"
      class="pt-10"
    >
      <NuxtLink :to="route.path + '#' + 'information'">
        {{ $t('productline.notes.title', [line.title]) }}
      </NuxtLink>
    </div>
  </UiSection>
</template>
